import { Button, FormControl, IconButton, makeStyles, MenuItem, Select } from '@material-ui/core';
import { SetStateAction, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { v4 } from 'uuid';
import { icon_peopleMinus, icon_peoplePlus } from '../../../assets/images/images';
import { CancelCrudButtons } from '../../../components/ui/CancelCrudButtons';
import { Modal } from '../../../components/ui/Modal';
import { TextArea } from '../../../components/ui/TextArea';
import { prettyDate } from '../../../config/dateFormatting';
import { IDishQuantiy } from '../../../interface/interface';
import { planEatApi } from '../../../planEatApi/PlanEatApi';
import { IBookingContent, IBookingItemContent, IDishContent, ISeatingContent, IVersionedObject } from '../../../planEatApi/PlanEatFacade';
import { clone } from '../../../utils/clone';

const useStyles = makeStyles((theme: any) => ({
    seatingDeleteContainer: {
        display: "grid",
        gridTemplateColumns: "100%",
        width: "100%",
    },
    title: {
        fontWeight: 'bold',
        marginBottom: 15,
    },
    inputSectionContainer: {
        display: "flex",
        flexDirection: "column",
        rowGap: 15
    },
    bookedInfoRow: {
        display: "flex",
        width: "50%",
        justifyContent: "space-between",
        alignItems: "center"
    },
    dishesContainer: {
        display: 'flex',
        rowGap: 15,
        flexDirection: 'row'
    },
    dishContainer: {
        display:'grid',
        gridTemplateColumns: '70% 30%',
        width: "50%",
        background: '#FFFFFF',
        boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)',
        borderLeft: '2px solid green',
        padding: 15,
    },
    numberOfPeopleContainer: {
        display: 'flex',
        justifyContent: "center",
        alignItems: 'center',
        width: 'min-content',
        '& > *':{
            padding: 0
        }
    },
    numberOfPeopleInput: {
        outline: 'none',
        border: 'none',
        display: 'flex',
        justifyContent: 'center',
        alignContent: 'center',
        margin: 'auto',
        fontSize: 26,
        textAlign: 'center',
        width: 40,
        '& > *':{
            display: 'flex',
            justifyContent: 'center',
            alignContent: 'center',
            width: '100%'
        }
    },
    buttonsContainer: {
        display: 'flex',
        width: '100%',
        columnGap: 15,
        justifyContent: 'flex-end'
    }
}));

interface Props {
    date: Date;
    bookedBy: string;
    seatingsOnDate: IVersionedObject<ISeatingContent>[];
    booking: IBookingContent;
    setBooking: React.Dispatch<SetStateAction<IBookingContent | undefined>>;
    updateBooking: (updatedBooking: IBookingContent) => void;
}

export const EditAdminBookingModal: React.FC<Props> = ({
    date, 
    bookedBy,
    seatingsOnDate, 
    booking,
    setBooking,
    updateBooking}) => {
    
    const classes = useStyles()
    const [hidden, setHidden] = useState(false)
    const [seatingId, setSeatingId] = useState("")
    const [comment, setComment] = useState(booking.bookingComment)
    const [dishesOnSeating, setDishesOnSeating] = useState<IVersionedObject<IDishContent>[]>([])
    const [dishQuantities, setDishQuantities] = useState<IDishQuantiy[]>([])

    useEffect(() => {
        if(hidden){
            setBooking(undefined)
        }
    },[hidden])

    useEffect(() => {
        if(booking && seatingId === ""){
            const tmpSeating = seatingsOnDate.find(item => item.content.id == booking?.seatingId)
            tmpSeating && setSeatingId(tmpSeating.content.id)
        }
    },[booking])

    useEffect(() => {
        if(seatingsOnDate.find(item => item?.content.id === booking.seatingId)){
            setSeatingId(booking.seatingId)
        }
    },[seatingsOnDate])

    function updateBookingInformation(){
        // Update seating selection
        if(seatingId !== ""){
            var tmpBooking = clone<IBookingContent>(booking)
            tmpBooking.seatingId = seatingId;
            tmpBooking.bookingComment = comment;
            tmpBooking.bookingItems = extractBookingItems()
            updateBooking(tmpBooking)
        }
    }

    function extractBookingItems(){
        let bookingItems: IBookingItemContent[] = []
        dishQuantities.forEach(dishQuantity => {
            for(var i=0;i<dishQuantity.quantity;i++){
                const bookingItem: IBookingItemContent = {
                    'bookingItemId': v4(),
                    'dishId': dishQuantity.dish.content.id,
                    'dishVersion': dishQuantity.dish.version
                };
                bookingItems.push(bookingItem)
            }
        })
        return bookingItems
    }

    function handleSeatingIdChange (event: any) {
        const tmpSeating = seatingsOnDate.find(item => item.content.id == event.target.value)
        if(tmpSeating){
            setSeatingId(tmpSeating.content.id)
        }
    };

    useEffect(() => {
        seatingsOnDate.forEach(seating => {
            if(seating.content.id === seatingId){
                planEatApi.getDishes()
                .then(response => {
                    var tmpDishesOnSeating: IVersionedObject<IDishContent>[] = []
                    response.data.forEach(dish => {
                        seating.content.seatingDishes.forEach(seatingDish => {
                            if(dish.content.id == seatingDish.dishId){
                                tmpDishesOnSeating.push(dish)
                            }
                        })
                    })
                    setDishesOnSeating(tmpDishesOnSeating)
                })
            }
        })
    },[seatingId])

    useEffect(() => {
        var tmpDishQuantities: IDishQuantiy[] = []
        dishesOnSeating.forEach(dishOnSeating => {
            tmpDishQuantities.push({
                dish: dishOnSeating,
                quantity: booking.bookingItems.length
            })
        })
        setDishQuantities(tmpDishQuantities)
    },[dishesOnSeating])

    function getSeatingDate(){
        const seatingTime = seatingsOnDate.find(item => item.content.id === seatingId)?.content.seatingStart
        return seatingTime ? seatingTime : new Date()
    }

    function updateDishQuantity(dishId: string, alterBy: number){
        var tmpDishQuantities: IDishQuantiy[] = []
        dishQuantities.forEach(dishQuantiy => {
            if(dishQuantiy.dish.content.id === dishId){
                tmpDishQuantities.push({
                    dish: dishQuantiy.dish,
                    quantity: dishQuantiy.quantity + alterBy
                })
            } else {
                tmpDishQuantities.push(dishQuantiy)
            }
        })
        setDishQuantities(tmpDishQuantities)
    }

    const handleQuantityChange = (dishId: string, strQuantity: string) => {
        var quantity = parseInt(strQuantity)
        if(Number.isNaN(quantity)){
            quantity = 0
        } else if(strQuantity.length > 1 && quantity === 0){
            quantity = 0
        } else if(quantity >= 99 || strQuantity.length > 2){
            quantity = 99
        } 

        var tmpDishQuantities: IDishQuantiy[] = []
        dishQuantities.forEach(dishQuantiy => {
            if(dishQuantiy.dish.content.id === dishId){
                tmpDishQuantities.push({
                    dish: dishQuantiy.dish,
                    quantity: quantity
                })
            } else {
                tmpDishQuantities.push(dishQuantiy)
            }
        })
        setDishQuantities(tmpDishQuantities)
    }

    useEffect(() => {
        console.log(dishQuantities)
    },[dishQuantities])

    return (
        <Modal 
            hidden={hidden} 
            setHidden={setHidden} 
            title='Edit booking' 
            >
                <div className={classes.seatingDeleteContainer}>
                    <div className={classes.inputSectionContainer}>
                        <div className={classes.bookedInfoRow}>
                            <div>
                                <div className={classes.title}>Booked at</div>
                                <div>{prettyDate(getSeatingDate(),false,true)}</div>
                            </div>
                            <div>
                                <div className={classes.title}>Booked by</div>
                                <div>{bookedBy}</div>
                            </div>
                        </div>
  
                        <div>
                        <div className={classes.title}>Choose a seating</div>
                            {
                                seatingId !== "" &&
                                <FormControl variant="outlined" style={{width: "50%"}}>
                                    <Select
                                        variant="outlined"
                                        value={seatingId}
                                        onChange={handleSeatingIdChange}
                                        fullWidth
                                    >
                                        {seatingsOnDate.map(seatingOnDate => <MenuItem key={seatingOnDate.content.id} value={seatingOnDate.content.id}>{seatingOnDate.content.seatingName} {prettyDate(seatingOnDate.content.seatingStart,true,false)}</MenuItem>)}
                                    </Select>
                                </FormControl>
                            }
                        </div>
                        <div>
                            <div className={classes.title}>Booking</div>
                            <div className={classes.dishesContainer}>
                                {dishQuantities.length > 0 ? dishQuantities.map(dishQuantity => 
                                    <div className={classes.dishContainer} key={dishQuantity.dish.content.id}>
                                        <div>
                                            <b>{dishQuantity.dish.content.dishTitle}</b>
                                            <div>{dishQuantity.dish.content.dishDescription}</div>
                                        </div>
                                        <div className={classes.numberOfPeopleContainer}>
                                            <IconButton onClick={() => {updateDishQuantity(dishQuantity.dish.content.id, -1)}}>
                                                <img src={icon_peopleMinus}/>
                                            </IconButton>
                                            <input 
                                                className={classes.numberOfPeopleInput} 
                                                value={dishQuantity.quantity} 
                                                type="number"
                                                onChange={(e) => {handleQuantityChange(dishQuantity.dish.content.id, e.target.value)}} 
                                                onBlur={(e) => {e.target.value=== "" && handleQuantityChange(dishQuantity.dish.content.id, "0")}}>
                                            
                                            </input>
                                            <IconButton color="secondary" onClick={() => {updateDishQuantity(dishQuantity.dish.content.id, 1)}}>
                                                <img src={icon_peoplePlus}/>
                                            </IconButton>
                                        </div>
                                    </div>
                                )
                                :
                                <div className={classes.dishContainer}>
                                    <div style={{height: 48}}>
                                        No dish on selected seating
                                    </div>
                                </div>
                                }
                            </div>
                        </div>
                        <div>
                            <div className={classes.title}>Comment</div>
                            <TextArea placeholder='Comments, notes etc' rows={2} defaultValue={comment} onBlur={(e) => setComment(e.target.value)} />
                        </div>
                    </div>
                </div>
                <CancelCrudButtons 
                    hidden={hidden} 
                    setHidden={setHidden} 
                    isDelete={false} 
                    postEntity={updateBookingInformation}
                    postButtonText={"Confirm Booking"}/>
        </Modal>
    )
}